// RUNTIME domain: runs, node runs (IA §40, backend §38–53).
// Global Runs page, Project Runs and Flow Runs are the same query with different filters.
import { db, byId, eq, sortBy, insert, update, ORG_ID, isLoaded, refreshTable } from './core';
import type { SeedRow } from './core';
import { flowGraph, executionPlan } from './flows';

export const canDeleteRun = (run: any, userId?: string) => !!run && !run.historical &&
  ['completed', 'failed', 'cancelled'].includes(run.status) && !!userId && run.started_by === userId;
export async function deleteExecution(id: string) {
  const response = await fetch(`/api/v1/executions/${encodeURIComponent(id)}`, { method:'DELETE' });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Could not delete execution (${response.status})`);
  fullRuns.delete(id);
  for (const page of runPages.values()) page.rows = page.rows.filter(run => run.id !== id);
  await refreshRuns();
  updated();
}

export const RUN_STATUSES = ['queued', 'running', 'completed', 'failed', 'cancelled'];
export async function saveExecutionOutput(runId: string, assetId: string) {
  const response = await fetch(`/api/v1/executions/${encodeURIComponent(runId)}/outputs/${encodeURIComponent(assetId)}/save-asset`, { method: 'POST' });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || 'Could not save output to Assets');
  return body.asset;
}
export const SCOPE_LABEL: any = { full_flow: 'Full flow', single_node: 'Single node', selected_nodes: 'Selected nodes', run_from_here: 'From here', group: 'Group' };

// Runs started in this browser session (simulated realtime). Pages poll these instead of a socket.
export const liveRunIds = new Set();
const liveHandles = new Map();
export function startLiveRun(opts?: any) {
  const handle = startRun({
    ...opts,
    onEvent(ev?: any) {
      if (['run.completed', 'run.failed', 'run.cancelled'].includes(ev.type)) { liveRunIds.delete(ev.run.id); liveHandles.delete(ev.run.id); }
      opts.onEvent?.(ev);
    },
  });
  liveRunIds.add(handle.run.id);
  liveHandles.set(handle.run.id, handle);
  return handle;
}

export function cancelRun(id?: any) {
  const h = liveHandles.get(id);
  if (h) return h.cancel();
  db.node_runs.filter((x?: any) => x.run_id === id && ['queued', 'running', 'waiting'].includes(x.status)).forEach((x?: any) => update('node_runs', x.id, { status: 'cancelled' }));
  update('runs', id, { status: 'cancelled', finished_at: new Date().toISOString() });
}

// The database only lets the starter update a run, so only they get a Cancel action.
export const canCancelRun = (run: any, userId?: string) => ['running', 'queued'].includes(run?.status) && !!userId && run.started_by === userId;

export function lastNodeRun(nodeId?: any) {
  const rows = db.node_runs.filter((nr?: any) => nr.node_id === nodeId && nr.started_at);
  return sortBy(rows, 'started_at')[0] || null;
}

// Run history is paged from the server instead of loaded whole. Each filter set keeps
// its own cursor; lists load more as they scroll. Totals come from the run_stats view
// and a run page reads that one run in full.
export interface RunFilters { workspaceId?: string; projectId?: string; flowId?: string; status?: string; userId?: string; trigger?: string; q?: string; since?: string; limit?: number; includeModels?: boolean }
interface RunPage { filters: RunFilters; rows: SeedRow[]; done: boolean; loading: Promise<void> | null; error: string }
const PAGE_SIZE = 50;
const runPages = new Map<string, RunPage>();
const fullRuns = new Map<string, SeedRow | null>();
const fullLoading = new Map<string, Promise<void>>();
const updated = () => window.dispatchEvent(new CustomEvent('cf:data-updated', { detail: 'runs' }));

const keyOf = (f: RunFilters) => JSON.stringify([f.workspaceId || '', f.projectId || '', f.flowId || '', f.status || '', f.userId || '',
  f.trigger || '', (f.q || '').trim().toLowerCase(), f.since || '', f.limit || PAGE_SIZE, !!f.includeModels]);
// Search covers flow and project names (resolved to flow IDs here) and the run number.
function searchedFlows(q: string) {
  const s = q.trim().toLowerCase();
  return db.flows.filter((f?: any) => `${f.name} ${byId('projects', f.project_id)?.name}`.toLowerCase().includes(s)).map((f?: any) => f.id);
}
function runQuery(f: RunFilters, before?: string): URLSearchParams | null {
  const params = new URLSearchParams({ limit: String(f.limit || PAGE_SIZE) });
  if (f.includeModels) params.set('models', '1');
  const entries: [string, string | undefined][] = [['workspace', f.workspaceId], ['project', f.projectId], ['flow', f.flowId], ['status', f.status],
    ['user', f.userId], ['source', f.trigger], ['since', f.since], ['before', before]];
  for (const [key, value] of entries) if (value) params.set(key, value);
  if (f.q?.trim()) {
    const flows = searchedFlows(f.q).slice(0, 150), number = f.q.trim().toLowerCase().replace(/^#/, '');
    if (flows.length) params.set('flows', flows.join(','));
    if (/^[0-9a-f]{1,8}$/.test(number)) params.set('number', number);
    if (!flows.length && !params.has('number')) return null;
  }
  return params;
}
async function fetchRuns(params: URLSearchParams): Promise<SeedRow[]> {
  const response = await fetch(`/api/v1/ui/runs?${params}`);
  if (!response.ok) throw new Error(`Cannot load runs (${response.status})`);
  const value: unknown = await response.json();
  if (!Array.isArray(value)) throw new Error('Invalid runs');
  return value as SeedRow[];
}
// fresh=true reloads the newest page and keeps older rows already scrolled into view.
function loadPage(filters: RunFilters, fresh = false): Promise<void> {
  const key = keyOf(filters);
  let page = runPages.get(key);
  if (!page) runPages.set(key, page = { filters, rows: [], done: false, loading: null, error: '' });
  if (page.loading) return page.loading;
  if (page.done && !fresh) return Promise.resolve();
  const current = page;
  const params = runQuery(filters, fresh ? undefined : current.rows.at(-1)?.created_at);
  current.loading = (async () => {
    try {
      const rows = params ? await fetchRuns(params) : [];
      const known = new Map(current.rows.map(row => [row.id, row]));
      const added = rows.filter(row => !known.has(row.id));
      if (fresh) {
        const fresher = new Map(rows.map(row => [row.id, row]));
        current.rows = [...added, ...current.rows.map(row => fresher.get(row.id) || row)];
        if (!current.rows.length || rows.length < (filters.limit || PAGE_SIZE)) current.done = true;
      } else {
        current.rows.push(...added);
        // A page with nothing new ends the list even if the server ignored the cursor.
        current.done = rows.length < (filters.limit || PAGE_SIZE) || !added.length;
      }
      current.error = '';
    } catch (error: any) {
      current.error = error.message;
      window.dispatchEvent(new CustomEvent('cf:data-error', { detail: error.message }));
    } finally { current.loading = null; updated(); }
  })();
  return current.loading;
}

function withScope(run: SeedRow) {
  // Older executions and test fixtures may carry only a flow ID; recover project and workspace from it.
  const projectId = run.project_id || (run.flow_id ? byId('flows', run.flow_id)?.project_id : null);
  const workspace = run.workspace_id || (projectId ? byId('projects', projectId)?.workspace_id : null);
  return {
    ...run,
    project_id: projectId,
    workspace_id: workspace,
    number: run.number || String(run.id).slice(0, 8),
    execution_scope: run.execution_scope || { type: 'full_flow' },
    trigger_type: run.trigger_type || run.trigger || run.source || 'manual',
    cost: run.cost ?? 0,
  };
}
function matches(run: SeedRow, { workspaceId, projectId, flowId, status, userId, trigger, q, since }: RunFilters) {
  if (workspaceId && run.workspace_id !== workspaceId) return false;
  if (projectId && run.project_id !== projectId) return false;
  if (flowId && run.flow_id !== flowId) return false;
  if (status && run.status !== status) return false;
  if (userId && run.started_by !== userId) return false;
  if (trigger && run.trigger_type !== trigger) return false;
  if (since && String(run.created_at) < since) return false;
  if (q && !`#${run.number} ${byId('flows', run.flow_id)?.name} ${byId('projects', run.project_id)?.name}`.toLowerCase().includes(q.trim().toLowerCase())) return false;
  return true;
}

/** Loaded runs for these filters, newest first. The first call starts loading the first page. */
export function listRuns(filters: RunFilters = {}): SeedRow[] {
  const page = runPages.get(keyOf(filters));
  if (!page) { void loadPage(filters); return []; }
  return page.rows.filter(run => run.organization_id === ORG_ID).map(withScope).filter(run => matches(run, filters)).slice(0, filters.limit || undefined);
}
export const loadRuns = (filters: RunFilters = {}) => loadPage(filters);
export const loadMoreRuns = (filters: RunFilters = {}) => loadPage(filters);
export function runListState(filters: RunFilters = {}) {
  const page = runPages.get(keyOf(filters));
  return { loading: !page || !!page.loading, done: !!page?.done, error: page?.error || '' };
}
export const hasActiveRuns = () => [...runPages.values()].some(page => page.rows.some(run => run.status === 'queued' || run.status === 'running'))
  || [...fullRuns.values()].some(run => run?.status === 'queued' || run?.status === 'running');
/** Reloads the newest rows of every list on screen, the open run pages and loaded totals. */
export function refreshRuns(): Promise<unknown> {
  const tables = (['run_stats', 'flow_last_runs'] as const).filter(isLoaded).map(table => refreshTable(table));
  return Promise.all([...[...runPages.values()].map(page => loadPage(page.filters, true)), ...[...fullRuns.keys()].map(id => loadRun(id)), ...tables]);
}
/** Drops cached run lists, e.g. after their workspace was deleted. */
export function forgetRuns() { runPages.clear(); fullRuns.clear(); updated(); }

export function loadRun(id: string): Promise<void> {
  if (fullLoading.has(id)) return fullLoading.get(id)!;
  const operation = (async () => {
    try {
      const rows = await fetchRuns(new URLSearchParams({ id }));
      fullRuns.set(id, rows.find(row => row.id === id) || null);
    } catch (error: any) {
      window.dispatchEvent(new CustomEvent('cf:data-error', { detail: error.message }));
      if (!fullRuns.has(id)) fullRuns.set(id, null);
    } finally { fullLoading.delete(id); updated(); }
  })();
  fullLoading.set(id, operation);
  return operation;
}
/** The full run with outputs; undefined while it loads, null when it does not exist. */
export function getRun(id?: any): SeedRow | null | undefined {
  if (!id) return null;
  if (!fullRuns.has(id)) { void loadRun(id); return undefined; }
  const run = fullRuns.get(id);
  return run ? withScope(run) : null;
}

export function runDetail(id?: any) {
  const run = getRun(id);
  if (!run) return { run:null, loading: run === undefined, nodeRuns:[], flow:null, project:null, version:null, user:null };
  const nodeRuns = eq('node_runs', 'run_id', id).map((nr?: any) => ({ ...nr, node: byId('nodes', nr.node_id), type: byId('node_types', nr.node_type_id) }));
  return { run, loading: false, nodeRuns, flow: byId('flows', run.flow_id), project: byId('projects', run.project_id), version: byId('flow_versions', run.flow_version_id), user: byId('users', run.started_by) };
}

export const failedNodeRun = (runId?: any) => eq('node_runs', 'run_id', runId).find((nr?: any) => nr.status === 'failed') || null;
export const runDuration = (r?: any) => (r.started_at && r.finished_at ? Date.parse(r.finished_at) - Date.parse(r.started_at) : r.started_at && r.status === 'running' ? Date.now() - Date.parse(r.started_at) : null);

// Totals are grouped by flow, user, trigger and status in the database.
function statRows({ workspaceId, projectId, projectIds, flowId, userId, trigger, q }: RunFilters & { projectIds?: string[] }) {
  const flows = q?.trim() ? new Set(searchedFlows(q)) : null;
  return db.run_stats.filter((r?: any) => (!workspaceId || r.workspace_id === workspaceId) && (!projectId || r.project_id === projectId)
    && (!projectIds || projectIds.includes(r.project_id)) && (!flowId || r.flow_id === flowId) && (!userId || r.user_id === userId)
    && (!trigger || r.source === trigger) && (!flows || flows.has(r.flow_id)));
}
export function runCounts(filters: RunFilters = {}) {
  const rows = statRows(filters);
  return Object.fromEntries(RUN_STATUSES.map((s?: any) => [s, rows.filter((r?: any) => r.status === s).reduce((n: number, r: any) => n + r.runs, 0)]));
}
export function runTotals(filters: RunFilters & { projectIds?: string[] } = {}) {
  const rows = statRows(filters);
  const count = (statuses?: string[]) => rows.filter((r?: any) => !statuses || statuses.includes(r.status)).reduce((n: number, r: any) => n + r.runs, 0);
  return { runs: count(), failed: count(['failed']), running: count(['running', 'queued']), completed: count(['completed']),
    cost: rows.reduce((sum: number, r: any) => sum + (r.cost || 0), 0) };
}

// ── Simulated execution (frontend-only stand-in for Orchestrator + realtime events) ──
// Emits the same event names as backend §90 so a WebSocket subscriber can replace it 1:1.
export function startRun({ flowId, scope = { type: 'full_flow' }, userId, failNodeName = null, onEvent }: any) {
  const { flow, nodes } = flowGraph(flowId);
  const levels = executionPlan(flowId, scope);
  // Runs are labelled by the start of their ID, like server runs.
  const id = crypto.randomUUID(), number = id.slice(0, 8);
  const now = new Date().toISOString();
  const run = insert('runs', {
    id, number, organization_id: flow.organization_id, workspace_id: flow.workspace_id, project_id: flow.project_id, flow_id: flowId,
    flow_version_id: flow.current_draft_version_id, status: 'queued', trigger_type: 'manual', execution_scope: scope, started_by: userId,
    input: {}, output: null, error: null, cost: 0, execution_snapshot: { flow_version_id: flow.current_draft_version_id, nodes: levels.flat().length },
    started_at: null, finished_at: null, created_at: now,
  });
  const inScope = new Set(levels.flat());
  nodes.forEach((n?: any, i?: any) => insert('node_runs', { id: `nrn_${id}_${i + 1}`, run_id: run.id, node_id: n.id, node_type_id: n.node_type_id, status: inScope.has(n.id) ? 'queued' : 'cached', cost: 0, started_at: null, finished_at: null, duration_ms: null }));
  const emit = (type?: any, payload?: any) => onEvent?.({ type, run, ...payload });
  emit('run.created', {});

  let cancelled = false;
  const timers: ReturnType<typeof setTimeout>[] = [];
  const at = (ms?: any, fn?: any) => timers.push(setTimeout(() => { if (!cancelled) fn(); }, ms));
  const nrOf = (nodeId?: any) => db.node_runs.find((x?: any) => x.run_id === run.id && x.node_id === nodeId);

  let t = 400;
  at(t, () => { update('runs', run.id, { status: 'running', started_at: new Date().toISOString() }); emit('run.started', {}); });
  let failed = false;
  levels.forEach((batch?: any) => {
    t += 200;
    const start = t;
    batch.forEach((nodeId?: any) => {
      const node = nodes.find((n?: any) => n.id === nodeId);
      const heavy = node.type.key.startsWith('video') ? 2600 : node.type.key.startsWith('image.generate') ? 1600 : 700;
      at(start, () => { if (failed) return; update('node_runs', nrOf(nodeId).id, { status: 'running', started_at: new Date().toISOString() }); emit('node.started', { nodeId }); });
      at(start + heavy / 2, () => { if (failed) return; emit('node.progress', { nodeId, progress: 0.5 }); });
      at(start + heavy, () => {
        if (failed) return;
        if (failNodeName && node.name === failNodeName) {
          failed = true;
          const error: any = { code: 'PROVIDER_ERROR', message: 'Provider returned 502', user_message: 'The model provider is temporarily unavailable.' };
          update('node_runs', nrOf(nodeId).id, { status: 'failed', error, finished_at: new Date().toISOString(), duration_ms: heavy });
          db.node_runs.filter((x?: any) => x.run_id === run.id && x.status === 'queued').forEach((x?: any) => update('node_runs', x.id, { status: 'skipped' }));
          update('runs', run.id, { status: 'failed', error, finished_at: new Date().toISOString() });
          emit('node.failed', { nodeId, error }); emit('run.failed', { error });
          return;
        }
        const cost = node.type.key.startsWith('video') ? 0.45 : node.type.key.startsWith('image.generate') ? 0.05 : 0.003;
        update('node_runs', nrOf(nodeId).id, { status: 'completed', cost, finished_at: new Date().toISOString(), duration_ms: heavy });
        update('runs', run.id, { cost: +(run.cost + cost).toFixed(4) });
        emit('node.completed', { nodeId });
      });
    });
    t += Math.max(...batch.map((id?: any) => { const k = nodes.find((n?: any) => n.id === id).type.key; return k.startsWith('video') ? 2600 : k.startsWith('image.generate') ? 1600 : 700; }));
  });
  at(t + 250, () => { if (failed) return; update('runs', run.id, { status: 'completed', finished_at: new Date().toISOString() }); emit('run.completed', {}); });

  return {
    run,
    cancel() {
      cancelled = true; timers.forEach(clearTimeout);
      db.node_runs.filter((x?: any) => x.run_id === run.id && ['queued', 'running'].includes(x.status)).forEach((x?: any) => update('node_runs', x.id, { status: 'cancelled' }));
      update('runs', run.id, { status: 'cancelled', finished_at: new Date().toISOString() });
      onEvent?.({ type: 'run.cancelled', run });
    },
  };
}
